import { Button, Tooltip, Divider } from '@heroui/react';
import { MousePointer2, Square, Ruler, PenTool, Pentagon, Triangle, Hash, Crosshair, Scaling } from 'lucide-react';
import type { ActiveTool } from '@/store/markupStore';

interface DrawingToolbarProps {
  activeTool: ActiveTool;
  onToolChange: (tool: ActiveTool) => void;
}

const TOOLS: { id: ActiveTool; label: string; shortcut: string; Icon: typeof MousePointer2 }[] = [
  { id: 'select', label: 'Select', shortcut: 'V', Icon: MousePointer2 },
  { id: 'area-rect', label: 'Area Rectangle', shortcut: 'R', Icon: Square },
  { id: 'area-polygon', label: 'Area Polygon', shortcut: 'P', Icon: Pentagon },
  { id: 'linear', label: 'Linear', shortcut: 'L', Icon: Ruler },
  { id: 'polyline', label: 'Polyline', shortcut: 'K', Icon: PenTool },
  { id: 'angle', label: 'Angle', shortcut: 'A', Icon: Triangle },
  { id: 'count', label: 'Count', shortcut: 'N', Icon: Hash },
];

const SCALE_TOOLS: { id: ActiveTool; label: string; shortcut: string; Icon: typeof MousePointer2 }[] = [
  { id: 'known-scale', label: 'Known Scale / Typicals', shortcut: 'S', Icon: Scaling },
  { id: 'calibrate', label: 'Line Scale', shortcut: 'C', Icon: Crosshair },
];

export default function DrawingToolbar({ activeTool, onToolChange }: DrawingToolbarProps) {
  const renderToolButton = ({ id, label, shortcut, Icon }: typeof TOOLS[number]) => {
    const isActive = activeTool === id;
    return (
      <Tooltip key={id} content={`${label} (${shortcut})`} placement="right" delay={400}>
        <Button
          isIconOnly
          size="sm"
          variant={isActive ? 'flat' : 'light'}
          onPress={() => onToolChange(id)}
          aria-label={label}
          className="min-w-8 w-8 h-8"
          style={{
            color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
            backgroundColor: isActive ? 'rgba(232, 160, 32, 0.15)' : undefined,
          }}
        >
          <Icon size={16} />
        </Button>
      </Tooltip>
    );
  };

  return (
    <div
      className="absolute top-4 left-4 flex flex-col gap-0.5 rounded-lg px-1 py-1 z-10"
      style={{
        backgroundColor: 'var(--bg-panel)',
        border: '1px solid var(--border)',
        backdropFilter: 'blur(8px)',
      }}
    >
      {TOOLS.map(renderToolButton)}
      <Divider className="my-1" />
      {SCALE_TOOLS.map(renderToolButton)}
    </div>
  );
}
